// Вхід: email + пароль (D34). Помилки — українською; після входу — на ?next або головну.
import { zodResolver } from '@hookform/resolvers/zod';
import { CalendarRange, LogIn } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';
import { ApiError, errorMessage } from '@/lib/api';
import { safeNext, useAuth } from '@/lib/auth';
import { Button, FormError, FormInput } from '@/ui';

const schema = z.object({
  email: z.string().trim().min(1, 'Вкажіть електронну пошту').email('Некоректна адреса електронної пошти'),
  password: z.string().min(1, 'Вкажіть пароль'),
});

type LoginForm = z.infer<typeof schema>;

function loginErrorText(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 401) return 'Невірна електронна пошта або пароль.';
    if (e.status === 403) return e.message === 'Недостатньо прав для цієї дії.' ? 'Обліковий запис вимкнено. Зверніться до адміністратора.' : e.message;
    if (e.status === 429) return 'Забагато спроб входу. Зачекайте кілька хвилин і спробуйте знову.';
  }
  return errorMessage(e, 'Не вдалося увійти. Спробуйте ще раз.');
}

export function LoginPage() {
  const { login, bootError } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [serverError, setServerError] = useState<string | null>(null);
  const form = useForm<LoginForm>({ resolver: zodResolver(schema), defaultValues: { email: '', password: '' } });

  useEffect(() => {
    document.title = 'Вхід · Розклад';
  }, []);

  const onSubmit = form.handleSubmit(async (values) => {
    setServerError(null);
    try {
      await login(values.email, values.password);
      navigate(safeNext(sp.get('next')), { replace: true });
    } catch (e) {
      setServerError(loginErrorText(e));
    }
  });

  return (
    <div className="flex min-h-dvh flex-col bg-bg lg:flex-row">
      <aside className="hidden flex-col justify-between bg-nav p-10 text-nav-fg lg:flex lg:w-[420px]">
        <div className="flex items-center gap-3">
          <span className="flex size-10 items-center justify-center rounded-field bg-primary-solid text-on-primary-solid" aria-hidden>
            <CalendarRange className="size-5" />
          </span>
          <span className="text-[17px] font-semibold">Розклад</span>
        </div>
        <div>
          <p className="text-[24px] leading-8 font-semibold">Система формування розкладу навчального процесу</p>
          <p className="mt-3 text-[14px] text-nav-muted">
            Довідники, навантаження, генерація розкладу з урахуванням корпусів і клінічних баз, конфлікти, погодження й публікація.
          </p>
        </div>
        <p className="text-[12px] text-nav-faint">Розробник — ТОВ «СОФТСОЛЕДЖ АЙТІ»</p>
      </aside>

      <main className="flex flex-1 items-center justify-center px-4 py-10">
        <div className="w-full max-w-sm">
          <div className="mb-6 flex items-center gap-2.5 lg:hidden">
            <span className="flex size-9 items-center justify-center rounded-field bg-primary-solid text-on-primary-solid" aria-hidden>
              <CalendarRange className="size-5" />
            </span>
            <span className="text-[16px] font-semibold">Розклад</span>
          </div>
          <h1 className="text-[22px] leading-8 font-semibold">Вхід</h1>
          <p className="mt-1 text-[14px] text-muted">Увійдіть обліковим записом, виданим адміністратором.</p>

          <form className="mt-6 flex flex-col gap-4" onSubmit={onSubmit} noValidate>
            <FormError message={serverError ?? bootError} />
            <FormInput control={form.control} name="email" label="Електронна пошта" type="email" autoComplete="username" inputMode="email" required autoFocus />
            <FormInput control={form.control} name="password" label="Пароль" type="password" autoComplete="current-password" required />
            <Button type="submit" variant="primary" icon={LogIn} loading={form.formState.isSubmitting} className="mt-1 w-full">
              Увійти
            </Button>
          </form>
          <p className="mt-6 text-[13px] text-muted">
            Забули пароль? Зверніться до системного адміністратора. Опублікований розклад доступний без входу —{' '}
            <a href="/p" className="font-semibold text-primary hover:text-primary-hover">
              переглянути
            </a>
            .
          </p>
        </div>
      </main>
    </div>
  );
}
